import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, Award, BookOpen, AlertCircle, CheckCircle2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Card } from '../ui/Card';
import { AppState, ModuleId } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/questionLoader';
import { AttentionCard } from './AttentionCard';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface TeacherOverviewTabProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
}
export function TeacherOverviewTab({
  students,
  selectedGroup,
  getModuleMastery
}: TeacherOverviewTabProps) {
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    let totalMastery = 0;
    let totalLevel = 0;
    let totalQuestions = 0;
    const strugglingStudents: Array<{
      student: StudentData;
      weakestModule: any;
      mastery: number;
    }> = [];
    students.forEach(student => {
      let studentTotalMastery = 0;
      let weakestModule = MODULES[0];
      let lowestMastery = 100;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        studentTotalMastery += mastery;
        if (mastery < lowestMastery) {
          lowestMastery = mastery;
          weakestModule = module;
        }
      });
      const avgStudentMastery = Math.round(studentTotalMastery / MODULES.length);
      totalMastery += avgStudentMastery;
      totalLevel += student.data.stats.level;
      totalQuestions += Object.keys(student.data.questionStates || {}).length;
      if (avgStudentMastery < 40 || lowestMastery < 30) {
        strugglingStudents.push({
          student,
          weakestModule,
          mastery: lowestMastery
        });
      }
    });
    const moduleAverages = MODULES.map(module => {
      const avg = Math.round(students.reduce((sum, s) => sum + getModuleMastery(s.id, module.id), 0) / students.length);
      return {
        module,
        avg
      };
    }).sort((a, b) => a.avg - b.avg);
    // Calculate weekly insights
    let studentsPracticedThisWeek = 0;
    const modulePracticeCounts = new Map<ModuleId, number>();
    students.forEach(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      const practicedDays = weeklyActivity.filter(d => d.practiced).length;
      if (practicedDays > 0) studentsPracticedThisWeek++;
      // Rough estimate of most practiced module (can be improved)
      Object.keys(student.data.questionStates || {}).forEach(key => {
        const state = student.data.questionStates[key];
        if (state.lastAttemptDate > Date.now() - 7 * 24 * 60 * 60 * 1000) {
          const moduleId = MODULES.find(m => key.startsWith(m.id.substring(0, 3).toUpperCase()))?.id;
          if (moduleId) {
            modulePracticeCounts.set(moduleId, (modulePracticeCounts.get(moduleId) || 0) + 1);
          }
        }
      });
    });
    let mostPracticedModuleId: ModuleId | null = null;
    let maxCount = 0;
    modulePracticeCounts.forEach((count, id) => {
      if (count > maxCount) {
        maxCount = count;
        mostPracticedModuleId = id;
      }
    });
    const mostPracticedModule = mostPracticedModuleId ? MODULES.find(m => m.id === mostPracticedModuleId) : null;
    const weakestClassModule = moduleAverages[0]?.module;
    return {
      avgMastery: Math.round(totalMastery / students.length),
      avgLevel: Math.round(totalLevel / students.length),
      totalQuestions,
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery).slice(0, 3),
      moduleAverages,
      weeklyInsight: {
        studentsPracticed: studentsPracticedThisWeek,
        mostPracticedModule,
        weakestClassModule
      }
    };
  }, [students, getModuleMastery]);
  if (!stats) return null;
  return <div className="space-y-8" data-id="element-5125">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5126">
        <StatCard icon={Users} label="Totaal Leerlingen" value={students.length} color="indigo" data-id="element-5127" />
        <StatCard icon={TrendingUp} label="Gem. Beheersing" value={`${stats.avgMastery}%`} color="emerald" data-id="element-5128" />
        <StatCard icon={Award} label="Gem. Level" value={stats.avgLevel} color="amber" data-id="element-5129" />
        <StatCard icon={BookOpen} label="Vragen Beantwoord" value={stats.totalQuestions} color="violet" data-id="element-5130" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5131">
        {/* Attention Needed Section */}
        <div className="lg:col-span-1 space-y-4" data-id="element-5132">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-5133">
            <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-5134" />
            Extra Aandacht Nodig
          </h3>

          {stats.strugglingStudents.length > 0 ? <div className="space-y-3" data-id="element-5135">
              {stats.strugglingStudents.map((item, index) => <motion.div key={item.student.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.1
          }} data-id="element-5136">
                  <AttentionCard studentName={item.student.name} weakestSubject={item.weakestModule.title} mastery={item.mastery} data-id="element-5137" />
                </motion.div>)}
            </div> : <Card className="p-6 bg-emerald-50 border-emerald-100 text-center" data-id="element-5138">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-5139">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" data-id="element-5140" />
              </div>
              <p className="font-bold text-emerald-900 mb-1" data-id="element-5141">
                Alles gaat goed! 🎉
              </p>
              <p className="text-sm text-emerald-700" data-id="element-5142">
                Geen leerlingen hebben momenteel extra aandacht nodig.
              </p>
            </Card>}
        </div>

        {/* Class Mastery by Subject */}
        <div className="lg:col-span-2" data-id="element-5143">
          <Card className="p-6" data-id="element-5144">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-5145">
              <TrendingUp className="w-5 h-5 text-indigo-500" data-id="element-5146" />
              Klasgemiddelde per Vak
            </h3>

            <div className="space-y-4" data-id="element-5147">
              {stats.moduleAverages.map((item, index) => <motion.div key={item.module.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className="flex items-center gap-4" data-id="element-5148">
                  <div className="w-40 flex items-center gap-2" data-id="element-5149">
                    {(() => {
                  const IconComp = (Icons as any)[item.module.icon] || Icons.BookOpen;
                  return <IconComp className="w-5 h-5 text-slate-500 flex-shrink-0" data-id="element-5150" />;
                })()}
                    <span className="text-sm font-bold text-slate-700 truncate" data-id="element-5151">
                      {item.module.title}
                    </span>
                  </div>
                  <div className="flex-1 flex items-center gap-3" data-id="element-5152">
                    <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-5153">
                      <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${item.avg}%`
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut'
                  }} className={`h-full rounded-full ${item.avg >= 80 ? 'bg-emerald-500' : item.avg >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} data-id="element-5154" />
                    </div>
                    <span className="text-sm font-black text-slate-700 w-10 text-right" data-id="element-5155">
                      {item.avg}%
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </Card>

          {/* Weekly Insight */}
          <Card className="p-6 mt-8 bg-indigo-50/50 border-indigo-100" data-id="element-5156">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-5157">
              <BookOpen className="w-5 h-5 text-indigo-500" data-id="element-5158" />
              Inzicht van deze week
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5159">
              <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm" data-id="element-5160">
                <div className="text-sm font-bold text-slate-500 mb-1" data-id="element-5161">
                  Geoefend
                </div>
                <div className="text-xl font-black text-indigo-700" data-id="element-5162">
                  {stats.weeklyInsight.studentsPracticed} / {students.length}{' '}
                  <span className="text-sm font-medium text-slate-600" data-id="element-5163">
                    leerlingen
                  </span>
                </div>
              </div>
              {stats.weeklyInsight.mostPracticedModule && <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm" data-id="element-5164">
                  <div className="text-sm font-bold text-slate-500 mb-1" data-id="element-5165">
                    Meest geoefend
                  </div>
                  <div className="text-lg font-bold text-slate-800 flex items-center gap-2" data-id="element-5166">
                    {(() => {
                  const IconComp = (Icons as any)[stats.weeklyInsight.mostPracticedModule!.icon] || Icons.BookOpen;
                  return <IconComp className="w-5 h-5 text-indigo-500" data-id="element-5167" />;
                })()}
                    {stats.weeklyInsight.mostPracticedModule.title}
                  </div>
                </div>}
              {stats.weeklyInsight.weakestClassModule && <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm" data-id="element-5168">
                  <div className="text-sm font-bold text-slate-500 mb-1" data-id="element-5169">
                    Aandachtspunt les
                  </div>
                  <div className="text-lg font-bold text-slate-800 flex items-center gap-2" data-id="element-5170">
                    {(() => {
                  const IconComp = (Icons as any)[stats.weeklyInsight.weakestClassModule!.icon] || Icons.BookOpen;
                  return <IconComp className="w-5 h-5 text-amber-500" data-id="element-5171" />;
                })()}
                    {stats.weeklyInsight.weakestClassModule.title}
                  </div>
                </div>}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}
function StatCard({
  icon: Icon,
  label,
  value,
  color
}: {
  icon: any;
  label: string;
  value: string | number;
  color: string;
}) {
  const colors: Record<string, string> = {
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    violet: 'bg-violet-50 text-violet-600 border-violet-100'
  };
  return <Card className="p-5 border-slate-200" data-id="element-5172">
      <div className="flex items-center justify-between mb-3" data-id="element-5173">
        <div className={`p-3 rounded-xl ${colors[color]} border`} data-id="element-5174">
          <Icon className="w-6 h-6" data-id="element-5175" />
        </div>
      </div>
      <div className="text-3xl font-black text-slate-900 mb-1" data-id="element-5176">{value}</div>
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5177">
        {label}
      </div>
    </Card>;
}